import { Dialog } from '@angular/cdk/dialog';
import { AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, ViewChild, computed, inject, input, output, signal, } from '@angular/core';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '@app/core/services/theme.service';
import { MediaApi, MessageApi } from '@app/data/api';
import { ChatNoteDto, ChatNoteType, FileSearchResultDto, FileType, McpApprovalRequestDto, MediaDto, MessageContentType, MessageDto, MessageType } from '@app/data/dto';
import { ConvertService } from '@app/services/convert.service';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { AppIconFile } from '@app/shared/controls/app-icon-file/app-icon-file';
import { ButtonDropdown, ButtonDropdownItem } from '@app/shared/controls/app-button-dropdown/app-button-dropdown';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MarkdownModule, MermaidAPI } from 'ngx-markdown';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppButtonConfirmCallout } from "@app/shared/controls/app-button-confirm-callout/app-button-confirm-callout";
import { ImageLightbox } from '@app/shared/modals/image-lightbox/image-lightbox';

type AssistantMessageAction = 'export-word' | 'speak';

interface MermaidRenderer {
  initialize(options: MermaidAPI.MermaidConfig): void;
  render(id: string, text: string): Promise<{ svg: string }> | { svg: string };
}

@Component({
  selector: 'app-message-balloon',
  imports: [AppIcon, ButtonDropdown, FormsModule, MarkdownModule, TextFieldModule, TranslatePipe, AppIconFile, AppButtonConfirmCallout],
  templateUrl: './message-balloon.html',
  styleUrl: './message-balloon.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageBalloon implements AfterViewChecked, OnDestroy {
  private static readonly IMAGE_GENERATION_MARKER = '[[SAFEGPT_IMAGE_GENERATION]]';
  private static readonly IMAGE_GENERATION_CANCELLED_MARKER = '[[SAFEGPT_IMAGE_GENERATION_CANCELLED]]';
  private static readonly MEDIA_ID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  readonly message = input.required<MessageDto>();
  readonly isLastAssistant = input(false);
  readonly notesToggled = output<string>();
  readonly sourcesToggled = output<string>();
  readonly messageEdited = output<{ messageId: string; text: string }>();
  readonly messageRegenerated = output<string>();
  readonly messageDeleted = output<string>();
  readonly wordExportRequested = output<string>();
  readonly mcpApprovalDecided = output<{ messageId: string; approved: boolean }>();
  @ViewChild('assistantMessageContent')
  private readonly assistantMessageContent?: ElementRef<HTMLElement>;
  @ViewChild('editTextarea')
  private readonly editTextarea?: ElementRef<HTMLTextAreaElement>;

  private readonly dialog = inject(Dialog);
  private readonly messageApi = inject(MessageApi);
  private readonly mediaApi = inject(MediaApi);
  private readonly convertService = inject(ConvertService);
  private readonly themeService = inject(ThemeService);
  private readonly translate = inject(TranslateService);
  private enhancedText = '';
  private shouldFocusEditor = false;
  private copyResetTimeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  private speechAudio?: HTMLAudioElement;
  private speechObjectUrl?: string;
  private speechSubscription?: Subscription;
  private readonly noteClock = signal(Date.now());
  private readonly noteClockIntervalId = globalThis.setInterval(() => this.noteClock.set(Date.now()), 1000);

  protected readonly MessageType = MessageType;
  protected readonly isUser = computed(() => this.message().messageType === MessageType.User);
  protected readonly messageText = computed(() => this.getMessageText(this.message()));
  protected readonly latestNote = computed(() => {
    const notes = this.message().notes ?? [];
    return [...notes].reverse().find((note) => !!note.header.trim()) ?? null;
  });
  protected readonly noteActivityActive = computed(() =>
    this.message().noteActivityInProgress === true || this.latestNote()?.status === 'active',
  );
  protected readonly mcpApproval = computed(() => this.message().mcpApproval ?? null);
  protected readonly sources = computed(() => this.message().sourceRef ?? []);
  protected readonly visibleSources = computed(() => this.sources().slice(0, 2));
  protected readonly media = computed(() => this.message().media ?? []);
  protected readonly visibleMedia = computed(() => this.media().slice(0, 2));
  protected readonly isEditing = signal(false);
  protected readonly editedText = signal('');
  protected readonly mediaOpen = signal(false);
  protected readonly downloadingMediaIds = signal<ReadonlySet<string>>(new Set<string>());
  protected readonly copiedToClipboard = signal(false);
  protected readonly isSpeechLoading = signal(false);
  protected readonly isSpeaking = signal(false);
  protected readonly isImageGenerationCancelled = computed(() =>
    this.messageText().startsWith(MessageBalloon.IMAGE_GENERATION_CANCELLED_MARKER),
  );
  protected readonly imageGenerationNote = computed(() => {
    const text = this.messageText();
    return text.startsWith(MessageBalloon.IMAGE_GENERATION_MARKER)
      ? text.slice(MessageBalloon.IMAGE_GENERATION_MARKER.length).trim()
      : null;
  });
  protected readonly isImageGenerationLoading = computed(() => this.imageGenerationNote() !== null);
  protected readonly isImageGenerationVisible = computed(() => this.isImageGenerationLoading() || this.isImageGenerationCancelled());
  protected readonly assistantMessageActions = computed<readonly ButtonDropdownItem[]>(() => [
    {
      id: 'export-word',
      label: this.translate.instant('chat.message.exportWord'),
      icon: 'icon-brand-word-color.svg'
    },
    {
      id: 'speak',
      label: this.translate.instant(this.isSpeechLoading() || this.isSpeaking() ? 'chat.message.stopReadingAnswer' : 'chat.message.speakMessage'),
      icon: 'icon-speaker-regular.svg',
      iconColor: 'currentColor',
    },
  ]);
  protected readonly mermaidOptions = computed<MermaidAPI.MermaidConfig>(() => ({
    suppressErrorRendering: true,
    theme: this.themeService.colorMode() === 'light' ? 'neutral' : 'dark',
  }));

  ngAfterViewChecked(): void {
    if (this.shouldFocusEditor) {
      this.shouldFocusEditor = false;
      this.editTextarea?.nativeElement.focus();
      this.editTextarea?.nativeElement.select();
    }

    if (this.message().messageType !== MessageType.Assistant) {
      return;
    }

    const text = this.messageText();
    if (!text || this.isImageGenerationVisible() || text === this.enhancedText) {
      return;
    }

    this.enhancedText = text;
    queueMicrotask(() => this.enhanceMarkdownBlocks(text));
    globalThis.setTimeout(() => this.enhanceMarkdownBlocks(text), 80);
  }

  ngOnDestroy(): void {
    if (this.copyResetTimeoutId != null) {
      globalThis.clearTimeout(this.copyResetTimeoutId);
    }

    globalThis.clearInterval(this.noteClockIntervalId);
    this.stopSpeech();
  }

  protected async copyMessage(): Promise<void> {
    if (this.copiedToClipboard() || this.isImageGenerationVisible()) {
      return;
    }

    const text = this.messageText().trim();
    if (!text) {
      return;
    }

    await this.writeClipboard(text, this.createMessageHtml(text));
    this.copiedToClipboard.set(true);

    if (this.copyResetTimeoutId != null) {
      globalThis.clearTimeout(this.copyResetTimeoutId);
    }

    this.copyResetTimeoutId = globalThis.setTimeout(() => {
      this.copiedToClipboard.set(false);
      this.copyResetTimeoutId = undefined;
    }, 3000);
  }

  protected onBodyClick(event: MouseEvent): void {
    if (this.isUser() && !this.isEditing()) {
      event.stopPropagation();
      this.startEditing();
    }
  }

  protected startEditingClick(event: MouseEvent): void {
    event.stopPropagation();
    this.startEditing();
  }

  protected startEditing(): void {
    this.editedText.set(this.messageText());
    this.isEditing.set(true);
    this.shouldFocusEditor = true;
  }

  protected cancelEditing(): void {
    this.isEditing.set(false);
    this.editedText.set('');
  }

  protected saveEditing(): void {
    const text = this.editedText().trim();
    if (!text || !this.hasEditChanges()) {
      return;
    }

    this.messageEdited.emit({ messageId: this.message().messageId, text });
    this.isEditing.set(false);
  }

  protected cancelEditingClick(event: MouseEvent): void {
    event.stopPropagation();
    this.cancelEditing();
  }

  protected saveEditingClick(event: MouseEvent): void {
    event.stopPropagation();
    this.saveEditing();
  }

  protected regenerateMessageClick(event: MouseEvent): void {
    event.stopPropagation();
    this.messageRegenerated.emit(this.message().messageId);
  }

  protected deleteMessageClick(): void {
    this.messageDeleted.emit(this.message().messageId);
  }

  protected toggleSpeechClick(event: MouseEvent): void {
    event.stopPropagation();

    this.toggleSpeech();
  }

  protected selectAssistantMessageAction(item: ButtonDropdownItem): void {
    const action = item.id as AssistantMessageAction;
    if (action === 'export-word') {
      this.wordExportRequested.emit(this.message().messageId);
      return;
    }

    this.toggleSpeech();
  }

  private toggleSpeech(): void {
    if (this.isSpeechLoading() || this.isSpeaking()) {
      this.stopSpeech();
      return;
    }

    this.playSpeech();
  }

  protected toggleNotesClick(event: MouseEvent): void {
    event.stopPropagation();
    this.notesToggled.emit(this.message().messageId);
  }

  protected decideMcpApproval(event: MouseEvent, approved: boolean): void {
    event.stopPropagation();
    if (this.mcpApproval()?.status !== 'pending') {
      return;
    }

    this.mcpApprovalDecided.emit({ messageId: this.message().messageId, approved });
  }

  protected mcpToolLabel(approval: McpApprovalRequestDto): string {
    const label = approval.toolName.replaceAll(/[_-]+/g, ' ').trim();
    return label
      ? label.charAt(0).toUpperCase() + label.slice(1)
      : this.translate.instant('chat.message.mcpApproval.unknownTool');
  }

  protected mcpArguments(approval: McpApprovalRequestDto): string | null {
    const value = approval.arguments?.trim();
    if (!value || value === '{}' || value === 'null') {
      return null;
    }

    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  protected toggleSourcesClick(event: MouseEvent): void {
    event.stopPropagation();
    this.sourcesToggled.emit(this.message().messageId);
  }

  protected toggleMediaClick(event: MouseEvent): void {
    event.stopPropagation();
    this.mediaOpen.update((isOpen) => !isOpen);
  }

  protected getMediaKind(media: MediaDto): string {
    const extension = media.fileName.split('.').pop()?.toUpperCase();
    if (extension) {
      return this.translate.instant('chat.messageBar.fileKind.extension', { extension });
    }

    return this.translate.instant('chat.messageBar.fileKind.file');
  }

  protected isMediaDownloading(mediaId: string): boolean {
    return this.downloadingMediaIds().has(mediaId);
  }

  protected isImageMedia(media: MediaDto): boolean {
    return media.fileType === FileType.Image;
  }

  protected async mediaClick(event: MouseEvent, attachment: MediaDto): Promise<void> {
    event.stopPropagation();

    if (this.isImageMedia(attachment)) {
      this.dialog.open(ImageLightbox, {
        data: attachment,
        maxWidth: '92vw',
        maxHeight: '88vh',
      });
      return;
    }

    if (this.isMediaDownloading(attachment.mediaId)) {
      return;
    }

    this.downloadingMediaIds.update((mediaIds) => new Set(mediaIds).add(attachment.mediaId));

    try {
      await this.downloadMediaFile(attachment.mediaId, attachment.fileName);
    } finally {
      this.downloadingMediaIds.update((mediaIds) => {
        const nextMediaIds = new Set(mediaIds);
        nextMediaIds.delete(attachment.mediaId);
        return nextMediaIds;
      });
    }
  }

  protected hasEditChanges(): boolean {
    return this.editedText().trim() !== this.messageText().trim();
  }

  private getMessageText(message: MessageDto): string {
    return message.contents
      ?.filter((content) => content.messageContentType === MessageContentType.Text && !!content.text?.trim())
      .sort((first, second) => new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime())
      .at(-1)?.text ?? '';
  }

  protected noteIcon(type: ChatNoteType): string {
    switch (type) {
      case 'webSearch':
        return 'fa-solid fa-globe';
      case 'fileSearch':
        return 'fa-regular fa-folder-open';
      case 'reasoning':
        return 'fa-regular fa-lightbulb';
      case 'codeInterpreter':
        return 'fa-solid fa-code';
      case 'hostedShell':
        return 'fa-solid fa-terminal';
      case 'toolOutput':
        return 'fa-solid fa-screwdriver-wrench';
    }
  }

  protected noteStatus(note: ChatNoteDto): string {
    if (note.status === 'active') {
      return note.header;
    }

    if (note.type === 'toolOutput') {
      return note.header;
    }

    return `Thought for ${this.formatDuration(this.totalNoteDuration())}`;
  }

  protected sourceIcon(source: FileSearchResultDto): string {
    const fileName = source.fileName.toLowerCase();
    if (source.url) return 'fa-solid fa-globe';
    if (fileName.endsWith('.pdf')) return 'fa-regular fa-file-pdf';
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'fa-regular fa-file-word';
    return 'fa-regular fa-file-lines';
  }

  private totalNoteDuration(): number {
    const message = this.message();
    if (message.reasoningDuration && message.reasoningDuration > 0) {
      return message.reasoningDuration;
    }

    const notes = message.notes ?? [];
    if (notes.length === 0) {
      return 0;
    }

    const startedAt = message.noteActivityStartedAt
      ? new Date(message.noteActivityStartedAt).getTime()
      : Math.min(...notes.map((note) => new Date(note.startedAt).getTime()).filter(Number.isFinite));
    const completedAt = this.noteActivityActive()
      ? this.noteClock()
      : Math.max(...notes.map((note) => new Date(note.completedAt ?? note.startedAt).getTime()).filter(Number.isFinite));
    if (!Number.isFinite(startedAt) || !Number.isFinite(completedAt)) {
      return 0;
    }

    return Math.max(1, Math.round((completedAt - startedAt) / 1000));
  }

  private formatDuration(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  private calculateDurationSeconds(startedAt: string | Date, completedAt: string | Date): number {
    return Math.max(1, Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000));
  }

  protected onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditing();
      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      this.saveEditing();
    }
  }

  private playSpeech(): void {
    this.stopSpeech();
    this.isSpeechLoading.set(true);

    this.speechSubscription = this.messageApi.speak(this.message().messageId).subscribe({
      next: (audioBlob) => {
        this.speechSubscription = undefined;
        this.isSpeechLoading.set(false);

        const objectUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(objectUrl);

        this.speechObjectUrl = objectUrl;
        this.speechAudio = audio;
        audio.addEventListener('ended', () => this.stopSpeech(), { once: true });
        audio.addEventListener('error', () => this.stopSpeech(), { once: true });

        this.isSpeaking.set(true);
        void audio.play().catch(() => this.stopSpeech());
      },
      error: () => {
        this.speechSubscription = undefined;
        this.isSpeechLoading.set(false);
        this.isSpeaking.set(false);
      },
    });
  }

  private stopSpeech(): void {
    this.speechSubscription?.unsubscribe();
    this.speechSubscription = undefined;
    this.isSpeechLoading.set(false);

    if (this.speechAudio) {
      this.speechAudio.pause();
      this.speechAudio.currentTime = 0;
      this.speechAudio = undefined;
    }

    if (this.speechObjectUrl) {
      URL.revokeObjectURL(this.speechObjectUrl);
      this.speechObjectUrl = undefined;
    }

    this.isSpeaking.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected closeUnchangedEditorOnOutsideClick(event: MouseEvent): void {
    if (!this.isEditing() || this.hasEditChanges()) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.editTextarea?.nativeElement.contains(target)) {
      return;
    }

    this.cancelEditing();
  }

  private enhanceMarkdownBlocks(text: string): void {
    const root = this.assistantMessageContent?.nativeElement;
    if (!root) {
      return;
    }

    this.decorateMermaidBlocks(root, text);
    this.decorateCodeBlocks(root);
    this.decorateTables(root);
    this.decorateMarkdownImages(root);
  }

  private decorateMermaidBlocks(root: HTMLElement, text: string): void {
    const mermaidElements = Array.from(root.querySelectorAll('.mermaid')) as HTMLElement[];
    const mermaidCodes = this.getMermaidCodeBlocksFromText(text);
    let mermaidCodeIndex = 0;

    mermaidElements.forEach((mermaidElement) => {
      if (mermaidElement.closest('.markdown-frame--mermaid')) {
        return;
      }

      const mermaidCode =
        mermaidCodes[mermaidCodeIndex] || (mermaidElement.textContent || '').trim();
      mermaidCodeIndex += 1;

      const frame = this.createFrame(this.translate.instant('chat.message.markdown.mermaid'));
      const copyRawLabel = this.translate.instant('chat.message.markdown.copyRaw');
      const copyRawButton = this.createFrameButton(copyRawLabel, async (button) => {
        await this.copyText(mermaidCode, button, copyRawLabel);
      });
      frame.actions.appendChild(copyRawButton);

      const downloadButton = this.createFrameButton(
        this.translate.instant('common.actions.download'),
        () => this.downloadMermaidPng(mermaidCode),
      );
      frame.actions.appendChild(downloadButton);

      const openButton = this.createFrameButton(
        this.translate.instant('chat.message.markdown.open'),
        () => this.openMermaidSvg(mermaidCode),
      );
      frame.actions.appendChild(openButton);

      frame.block.classList.add('markdown-frame--mermaid');
      mermaidElement.parentNode?.insertBefore(frame.block, mermaidElement);
      frame.content.appendChild(mermaidElement);
      frame.block.appendChild(frame.toolbar);
      frame.block.appendChild(frame.content);
    });
  }

  private decorateCodeBlocks(root: HTMLElement): void {
    const preElements = Array.from(root.querySelectorAll('pre')) as HTMLElement[];

    preElements.forEach((preElement) => {
      if (preElement.closest('.markdown-frame--code')) {
        return;
      }

      const codeElement = preElement.querySelector('code') as HTMLElement | null;
      const codeText = (codeElement?.innerText || preElement.innerText || '').trim();
      const frame = this.createFrame(this.getCodeLanguageLabel(codeElement));
      const copyLabel = this.translate.instant('chat.message.markdown.copy');
      const copyButton = this.createFrameButton(copyLabel, async (button) => {
        await this.copyText(codeText, button, copyLabel);
      });

      frame.block.classList.add('markdown-frame--code');
      frame.actions.appendChild(copyButton);
      preElement.parentNode?.insertBefore(frame.block, preElement);
      frame.content.appendChild(preElement);
      frame.block.appendChild(frame.toolbar);
      frame.block.appendChild(frame.content);
    });
  }

  private decorateTables(root: HTMLElement): void {
    const tables = Array.from(root.querySelectorAll('table')) as HTMLTableElement[];

    tables.forEach((table) => {
      if (table.closest('.markdown-frame--table')) {
        return;
      }

      const frame = this.createFrame(this.translate.instant('chat.message.markdown.table'));
      const copyLabel = this.translate.instant('chat.message.markdown.copy');
      const copyButton = this.createFrameButton(copyLabel, async (button) => {
        await this.copyHtml(this.tableToPlainText(table), table.outerHTML, button, copyLabel);
      });

      frame.block.classList.add('markdown-frame--table');
      frame.actions.appendChild(copyButton);
      table.parentNode?.insertBefore(frame.block, table);
      frame.content.appendChild(table);
      frame.block.appendChild(frame.toolbar);
      frame.block.appendChild(frame.content);
    });
  }

  private decorateMarkdownImages(root: HTMLElement): void {
    const images = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];

    images.forEach((image) => {
      if (image.closest('.markdown-frame--image')) {
        return;
      }

      const frame = this.createFrame(this.getImageTitle(image));
      const downloadButton = this.createFrameButton(
        this.translate.instant('common.actions.download'),
        () => this.downloadImage(image),
      );

      frame.block.classList.add('markdown-frame--image');
      frame.actions.appendChild(downloadButton);

      let imageContainer: HTMLElement = image;
      const paragraphParent = image.parentElement;
      if (
        paragraphParent?.tagName === 'P' &&
        paragraphParent.childElementCount === 1 &&
        !(paragraphParent.textContent || '').trim()
      ) {
        imageContainer = paragraphParent;
      }

      imageContainer.parentNode?.insertBefore(frame.block, imageContainer);
      frame.content.appendChild(imageContainer);
      frame.block.appendChild(frame.toolbar);
      frame.block.appendChild(frame.content);
    });
  }

  private createFrame(titleText: string): {
    block: HTMLDivElement;
    toolbar: HTMLDivElement;
    actions: HTMLDivElement;
    content: HTMLDivElement;
  } {
    const block = document.createElement('div');
    block.className = 'markdown-frame';

    const toolbar = document.createElement('div');
    toolbar.className = 'markdown-frame__toolbar';

    const title = document.createElement('div');
    title.className = 'markdown-frame__title';
    title.textContent = titleText;

    const actions = document.createElement('div');
    actions.className = 'markdown-frame__actions';

    const content = document.createElement('div');
    content.className = 'markdown-frame__content';

    toolbar.appendChild(title);
    toolbar.appendChild(actions);

    return { block, toolbar, actions, content };
  }

  private createFrameButton(
    label: string,
    onClick: (button: HTMLButtonElement) => void | Promise<void>,
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'markdown-frame__button';
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', () => void onClick(button));
    return button;
  }

  private async copyText(text: string, button: HTMLButtonElement, label: string): Promise<void> {
    if (!text) {
      return;
    }

    await this.writeClipboard(text, null);
    button.textContent = this.translate.instant('chat.message.markdown.copied');
    globalThis.setTimeout(() => {
      button.textContent = label;
    }, 1200);
  }

  private async copyHtml(text: string, html: string, button: HTMLButtonElement, label: string): Promise<void> {
    if (!text && !html) {
      return;
    }

    await this.writeClipboard(text, html);
    button.textContent = this.translate.instant('chat.message.markdown.copied');
    globalThis.setTimeout(() => {
      button.textContent = label;
    }, 1200);
  }

  private async writeClipboard(text: string, html: string | null): Promise<void> {
    if (html && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return;
    }

    await navigator.clipboard?.writeText(text);
  }

  private createMessageHtml(text: string): string {
    if (!this.isUser() && this.assistantMessageContent?.nativeElement) {
      const clone = this.assistantMessageContent.nativeElement.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('.markdown-frame__toolbar').forEach((element) => element.remove());
      const html = clone.innerHTML.trim();
      if (html) {
        return html;
      }
    }

    return `<div>${this.escapeHtml(text).replaceAll('\n', '<br>')}</div>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private async openMermaidSvg(mermaidCode: string): Promise<void> {
    const svgText = await this.renderDefaultMermaidSvg(mermaidCode);
    if (!svgText) {
      return;
    }

    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    globalThis.open(url, '_blank');
    globalThis.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private async downloadMermaidPng(mermaidCode: string): Promise<void> {
    const svg = await this.renderDefaultMermaidSvgElement(mermaidCode);
    if (!svg) {
      return;
    }

    const pngBlob = await this.convertService.convertSvgToPng(svg, { backgroundColor: '#ffffff', scale: 2 });
    this.downloadBlob(pngBlob, 'mermaid-diagram.png');
  }

  private async renderDefaultMermaidSvgElement(mermaidCode: string): Promise<SVGSVGElement | null> {
    const svgText = await this.renderDefaultMermaidSvg(mermaidCode);
    if (!svgText) {
      return null;
    }

    const svgDocument = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    return svgDocument.querySelector('svg');
  }

  private async renderDefaultMermaidSvg(mermaidCode: string): Promise<string | null> {
    const mermaid = this.getMermaidRenderer();
    if (!mermaid || !mermaidCode.trim()) {
      return null;
    }

    mermaid.initialize({ startOnLoad: false, suppressErrorRendering: true, theme: 'neutral' });
    const renderId = `mermaid-export-${globalThis.crypto?.randomUUID?.() ?? Date.now()}`;
    const result = await mermaid.render(renderId, mermaidCode);
    return result.svg;
  }

  private getMermaidRenderer(): MermaidRenderer | null {
    const globalWithMermaid = globalThis as typeof globalThis & { mermaid?: MermaidRenderer };
    return globalWithMermaid.mermaid?.render ? globalWithMermaid.mermaid : null;
  }

  private getMermaidCodeBlocksFromText(text: string): string[] {
    const marker = '```mermaid';
    const blocks: string[] = [];
    let markerIndex = text.indexOf(marker);
    while (markerIndex >= 0) {
      const contentStart = markerIndex + marker.length;
      const contentEnd = text.indexOf('```', contentStart);
      if (contentEnd < 0) break;
      blocks.push(text.slice(contentStart, contentEnd).trim());
      markerIndex = text.indexOf(marker, contentEnd + 3);
    }
    return blocks;
  }

  private getCodeLanguageLabel(codeElement: HTMLElement | null): string {
    const className = codeElement?.className || '';
    const languageMatch = /language-([a-zA-Z0-9_-]+)/.exec(className);
    if (!languageMatch) {
      return this.translate.instant('chat.message.markdown.code');
    }

    const rawLanguage = languageMatch[1].replaceAll(/[-_]/g, ' ');
    return rawLanguage.charAt(0).toUpperCase() + rawLanguage.slice(1);
  }

  private tableToPlainText(table: HTMLTableElement): string {
    return Array.from(table.querySelectorAll('tr'))
      .map((row) =>
        Array.from(row.querySelectorAll('th, td'))
          .map((cell) => (cell.textContent || '').trim())
          .join('\t'),
      )
      .join('\n')
      .trim();
  }

  private getImageTitle(image: HTMLImageElement): string {
    const alt = image.alt.trim();
    if (alt) {
      return alt;
    }

    try {
      const parsedUrl = new URL(image.currentSrc || image.src, globalThis.location.origin);
      const fileName = decodeURIComponent(
        parsedUrl.pathname.substring(parsedUrl.pathname.lastIndexOf('/') + 1),
      );
      return fileName || this.translate.instant('chat.message.markdown.image');
    } catch {
      return this.translate.instant('chat.message.markdown.image');
    }
  }

  private async downloadImage(image: HTMLImageElement): Promise<void> {
    const media = this.findMediaForImage(image);
    if (media) {
      await this.downloadMediaFile(media.mediaId, media.fileName || this.getImageTitle(image));
      return;
    }

    await this.downloadImageUrl(image.currentSrc || image.src, this.getImageTitle(image));
  }

  private async downloadImageUrl(url: string, fileName: string): Promise<void> {
    if (!url) {
      return;
    }

    const response = await fetch(url);
    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    this.downloadBlob(blob, fileName);
  }

  private async downloadMediaFile(mediaId: string, fallbackFileName: string): Promise<void> {
    const response = await firstValueFrom(this.mediaApi.downloadFile(mediaId));
    const blob = response.body;
    if (!blob) {
      return;
    }

    const fileName = this.getFileNameFromContentDisposition(response.headers.get('content-disposition'))
      ?? fallbackFileName;
    this.downloadBlob(blob, fileName);
  }

  private getFileNameFromContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8FileNameMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8FileNameMatch?.[1]) {
      return decodeURIComponent(utf8FileNameMatch[1].trim().replace(/^"|"$/g, ''));
    }

    const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return fileNameMatch?.[1]?.trim() || null;
  }

  private findMediaForImage(image: HTMLImageElement): MediaDto | null {
    const imageUrl = image.currentSrc || image.src;
    const media = this.getMessageMedia();

    return media.find((item) =>
      (!!item.mediaUrl && item.mediaUrl === imageUrl)
      || (!!item.fileName && item.fileName === image.alt)
      || this.isMediaIdInUrl(imageUrl, item.mediaId),
    ) ?? null;
  }

  private getMessageMedia(): MediaDto[] {
    const media = [...(this.message().media ?? [])];
    for (const content of this.message().contents ?? []) {
      if (content.media) {
        media.push(content.media);
      }
    }

    return media;
  }

  private isMediaIdInUrl(url: string, mediaId: string): boolean {
    try {
      const parsedUrl = new URL(url, globalThis.location.origin);
      const value = parsedUrl.protocol === 'sandbox:'
        ? parsedUrl.pathname.split(':').at(-1)
        : parsedUrl.pathname.split('/').at(-1);
      return value === mediaId && MessageBalloon.MEDIA_ID_PATTERN.test(value);
    } catch {
      return false;
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    this.downloadUrl(url, fileName);
    globalThis.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private downloadUrl(url: string, fileName: string, target?: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    if (target) {
      link.target = target;
    }
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
